import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { api, type Project, mediaSrc } from "../lib/api";
import { formatProjectMonthYear } from "../lib/dates";
import { usePageTitle } from "../lib/pageTitle";
import { VimeoEmbed } from "../components/VimeoEmbed";
import "./ProjectPage.scss";

export function ProjectPage() {
  const { slug } = useParams();
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lbIndex, setLbIndex] = useState(-1);

  usePageTitle(project?.title || (error ? "Not found" : "Project"));

  useEffect(() => {
    if (!slug) return;
    setProject(null);
    setError(null);
    api
      .project(slug)
      .then((p) => {
        setProject(p);
        // Open Graph-ish meta updates
        const setMeta = (prop: string, content: string) => {
          let el = document.querySelector(`meta[property="${prop}"]`);
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute("property", prop);
            document.head.appendChild(el);
          }
          el.setAttribute("content", content);
        };
        setMeta("og:title", p.title);
        setMeta("og:description", p.shortDescription || p.description.slice(0, 160));
        const img = p.thumbnail?.url || p.media?.find((m) => m.type === "image")?.url;
        setMeta("og:image", img ? mediaSrc(img) : `${window.location.origin}/og-image.png`);
      })
      .catch((e) => setError(e.message));
  }, [slug]);

  if (error) {
    return (
      <div className="container project-page">
        <p className="project-page__err">{error}</p>
        <Link to="/portfolio">← Back to portfolio</Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container project-page">
        <p className="project-page__loading">Loading…</p>
      </div>
    );
  }

  const images = (project.media || []).filter((m) => m.type === "image");
  const videos = (project.media || []).filter((m) => m.type === "video");
  const slides = images.map((m) => ({ src: mediaSrc(m.url) }));
  const poster =
    mediaSrc(project.thumbnail?.thumbnailUrl || project.thumbnail?.url) ||
    mediaSrc(images[0]?.thumbnailUrl || images[0]?.url) ||
    "";

  return (
    <article className="project-page page-enter">
      <div className="container">
        <Link to="/portfolio" className="project-page__back">
          ← Portfolio
        </Link>
        <header className="project-page__header">
          <div className="project-page__cats">
            {project.categories.map((c) => (
              <Link key={c.id} to={`/portfolio/${c.slug}`} className="badge">
                {c.name}
              </Link>
            ))}
          </div>
          <h1>{project.title}</h1>
          <div className="project-page__meta">
            {project.date && (
              <span>{formatProjectMonthYear(project.date)}</span>
            )}
            {project.software.length > 0 && (
              <span>{project.software.join(" · ")}</span>
            )}
          </div>
        </header>

        {project.description && (
          <div className="project-page__desc">
            {project.description.split("\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}

        {videos.length > 0 && (
          <div className="project-page__videos">
            {videos.map((v) => {
              const watchUrl =
                v.externalUrl ||
                (v.videoSource === "vimeo" && v.videoId
                  ? `https://vimeo.com/${v.videoId}`
                  : v.url);
              const vimeoId =
                v.videoId ||
                (v.url.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1] ?? null);

              return (
                <div key={v.id} className="project-page__video">
                  {v.videoSource === "vimeo" && vimeoId ? (
                    <VimeoEmbed
                      videoId={vimeoId}
                      title={v.caption || project.title}
                      watchUrl={watchUrl}
                      poster={poster}
                    />
                  ) : v.videoSource === "youtube" ? (
                    <iframe
                      src={mediaSrc(v.url)}
                      title={v.caption || project.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  ) : v.videoSource === "self_hosted" ? (
                    <video
                      src={mediaSrc(v.url)}
                      controls
                      playsInline
                      poster={poster || undefined}
                    />
                  ) : (
                    <a
                      href={watchUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="project-page__video-link"
                    >
                      {poster ? (
                        <img src={poster} alt="" className="project-page__video-poster" />
                      ) : (
                        <div className="project-page__video-poster project-page__video-poster--empty" />
                      )}
                      <span className="project-page__video-cta">
                        <span className="project-page__play" aria-hidden>
                          ▶
                        </span>
                        Watch video ↗
                      </span>
                    </a>
                  )}
                  {v.caption &&
                    !String(v.caption).includes("opens on Vimeo") &&
                    !String(v.caption).includes("embedding disabled") && (
                      <p>{v.caption}</p>
                    )}
                </div>
              );
            })}
          </div>
        )}

        <div className="project-page__gallery">
          {images.map((m, i) => (
            <button
              key={m.id}
              type="button"
              className="project-page__shot"
              onClick={() => setLbIndex(i)}
              aria-label={`Open image ${i + 1}`}
            >
              <img
                src={mediaSrc(m.thumbnailUrl || m.url)}
                alt={m.alt || project.title}
                loading="lazy"
              />
            </button>
          ))}
        </div>

        {(project.tags.length > 0 || project.externalLinks.length > 0) && (
          <footer className="project-page__footer">
            {project.tags.length > 0 && (
              <div className="project-page__tags">
                {project.tags.map((t) => (
                  <Link key={t.id} to={`/portfolio?tag=${encodeURIComponent(t.name)}`}>
                    #{t.name}
                  </Link>
                ))}
              </div>
            )}
            {project.externalLinks.length > 0 && (
              <div className="project-page__links">
                {project.externalLinks.map((l) => (
                  <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="btn btn--ghost btn--sm">
                    {l.label} ↗
                  </a>
                ))}
              </div>
            )}
          </footer>
        )}
      </div>

      <Lightbox
        open={lbIndex >= 0}
        index={lbIndex}
        close={() => setLbIndex(-1)}
        slides={slides}
      />
    </article>
  );
}
